import { useState } from "react";
import { toast } from "sonner";
import { createContact } from "@/lib/api";
import { Mail, MapPin, Loader2 } from "lucide-react";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const INITIAL = { name: "", email: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await createContact(form);
      setForm(INITIAL);
      toast.success("Message sent. We'll reply within 2 business days.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send message.";
      toast.error(typeof msg === "string" ? msg : "Network error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-y-10 md:gap-10">
          {/* Left: Details */}
          <div className="col-span-12 lg:col-span-5">
            <div className="overline text-[#FF2A00] mb-4">/ 08 — Contact Us</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter">
              Talk to us.
            </h2>
            <p className="mt-6 text-zinc-700 max-w-md">
              Press, sponsorships, speaking proposals and delegate concierge — write to
              the right team and we'll route you in under 24 hours.
            </p>

            <div className="mt-10 space-y-5">
              <InfoRow icon={Mail} label="General">
                <a
                  href="mailto:info@botsummits.com"
                  data-testid="contact-email"
                  className="hover:text-[#002FA7]"
                >
                  info@botsummits.com
                </a>
              </InfoRow>
              <InfoRow icon={Mail} label="Sponsorships">
                <a href="mailto:info@botsummits.com" className="hover:text-[#002FA7]">
                  info@botsummits.com
                </a>
              </InfoRow>
              <InfoRow icon={WhatsAppIcon} label="Delegate Desk">
                <a href="https://wa.me/447915361665" target="_blank" rel="noopener noreferrer" data-testid="contact-phone-uk" className="hover:text-[#002FA7]">
                  +44 7915361665
                </a>
                <br />
                <a href="https://wa.me/918825035701" target="_blank" rel="noopener noreferrer" data-testid="contact-phone" className="hover:text-[#002FA7]">
                  +91 8825035701
                </a>
                <br />
                <a href="https://wa.me/971525330661" target="_blank" rel="noopener noreferrer" data-testid="contact-phone-uae" className="hover:text-[#002FA7]">
                  +971 525330661
                </a>
              </InfoRow>
              <InfoRow icon={MapPin} label="Secretariat">
                <span className="block mb-2" data-testid="contact-address-uk">
                  88 Wood Street
                  <br /> London, E17 3NU
                </span>
                D-2, District Centre, Saket,
                <br /> New Delhi, 110017, IN
                <br />
                <span className="block mt-2">
                  318, Ruby Towers, Silicon Oasis,
                  <br /> Dubai, UAE - 73507
                </span>
              </InfoRow>
            </div>

            <div className="mt-10 border border-zinc-200 p-5 bg-zinc-50">
              <div className="overline text-zinc-500 mb-2">Office hours</div>
              <div className="font-mono text-sm">Mon — Fri · 09:00 — 18:00</div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="col-span-12 lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-zinc-200 p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <Field
                label="Name"
                testid="contact-name"
                required
                value={form.name}
                onChange={onChange("name")}
                placeholder="Your name"
              />
              <Field
                label="Email"
                testid="contact-email-field"
                required
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="you@company.com"
              />
              <Field
                label="Subject"
                testid="contact-subject"
                required
                value={form.subject}
                onChange={onChange("subject")}
                placeholder="Sponsorship inquiry"
                className="md:col-span-2"
              />
              <div className="md:col-span-2">
                <label className="overline text-zinc-500 mb-2 block">Message</label>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell us a bit about your organization and what you're looking for…"
                  className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="md:col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-black text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-[#002FA7] transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>Send Message →</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, className = "", ...rest }) => (
  <div className={className}>
    <label className="overline text-zinc-500 mb-2 block">{label}</label>
    <input
      data-testid={testid}
      className="w-full bg-white border border-zinc-300 px-4 py-3 text-sm font-body focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
      {...rest}
    />
  </div>
);

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center shrink-0">
      <Icon size={16} className="text-[#002FA7]" />
    </div>
    <div>
      <div className="overline text-zinc-500 mb-1">{label}</div>
      <div className="text-sm text-zinc-800 leading-relaxed">{children}</div>
    </div>
  </div>
);

export default Contact;
